import { AxiosInstance } from "axios";
import { EmailStatus } from "../types/SavedDocument";
import { SendForReviewEvent } from "../dbot/common/commonDomain";
import { Language } from "../i18n/languages";
import { PhoneNumber, SignerType, SigningMethod } from "../common/common";
import { SubscriberId } from "../dbot/increaseOfShareCapital/increaseOfShareCapitalDomain";
import { SigningSettings } from "../common/commonForms";
import { ShareTypeCode } from "../common/share";
import { DCEvent } from "../events/eventsDomain";
import { RootStoreData } from "../events/rootStoreData";
import { UploadedAttachment } from "../fileUpload/fileUploadDomain";

export enum DocumentSigningStatus {
  UNKNOWN = "UNKNOWN",
  SENT_FOR_SIGNING = "SENT_FOR_SIGNING",
  SENT_FOR_REVIEW = "SENT_FOR_REVIEW",
  SENT_FOR_VOTING = "SENT_FOR_VOTING",
  NOT_NEEDED = "NOT_NEEDED",
  NEEDS_SIGNING = "NEEDS_SIGNING",
  SIGNING_IN_PROGRESS = "SIGNING_IN_PROGRESS",
  SIGNING_COMPLETE = "SIGNING_COMPLETE",
}
export type SigningStatusForSigner = {
  signedDocumentId: string;
  reference: string;
  email: string;
  name: string;
  phoneNumber?: PhoneNumber;
  language?: Language;
  signingMethod: SigningMethod;
  signerType: SignerType;
  isSigned: boolean;
  signingTime?: string;
  sendingStatus: EmailStatus;
  sendingTime?: string;
  documentId: string;
  orgNo: string;
  companyName: string;
  signingUrl?: string;
};

export type GetDocumentSigningStatusResponse = {
  documentSigningStatus: DocumentSigningStatus;
  signerStatus: Array<SigningStatusForSigner>;
  numberOfSignedDocuments: number;
};

export type SubscriptionStatus = {
  signedSubscriptionDocumentId: string;
  subscriberId: SubscriberId;
  name: string;
  shareTypeId: number;
  shareTypeCode: ShareTypeCode;
  availableShares: number;
  numberOfShares: number;
  isSigned: boolean;
  signingTime?: string;
};
export type GetSubscriptionStatusResponse = {
  subscriptions: Array<SubscriptionStatus>;
};

export type SendUploadedDocumentForSigningRequest = {
  documentId: string;
  signers: {
    signerName: string;
    email: string;
    phoneNumber?: PhoneNumber;
    signingMethod: SigningMethod;
    language?: Language;
    reference?: string;
  }[];
  emailBody?: string;
  signingSettings: SigningSettings;
};

export type ResendDocumentForSigningRequest = {
  name: string;
  email: string;
  phoneNumber?: PhoneNumber;
  signingMethod: SigningMethod;
  language?: Language;
};

export type GetDocumentForViewingResponse = {
  documentId: string;
  title: string;
  preview: string;
  senderName?: string;
  orgNo: string;
  companyName: string;
  recipientName: string;
  recipientInitials: string;
  recipientEmail?: string;
  attachments: UploadedAttachment[];
  downloadPdfUrl: string;
  downloadWithAttachmentsUrl?: string;
};

export type DocumentForViewingState =
  | { type: "pending" }
  | { type: "failure"; err?: unknown }
  | DocumentForViewingReadyState;

export type DocumentForViewingReadyState = {
  type: "ready";
  data: GetDocumentForViewingResponse;
  isDownloading?: boolean;
};

export class DocumentApiClient {
  readonly http: AxiosInstance;
  readonly organisationNumber: string;

  constructor(http: AxiosInstance, organisationNumber: string) {
    this.http = http;
    this.organisationNumber = organisationNumber;
  }

  getSigningStatus(params: { documentId: string }): Promise<GetDocumentSigningStatusResponse> {
    return this.http
      .get<GetDocumentSigningStatusResponse>(`/document/check-signing-status/${params.documentId}`)
      .then((res) => res.data);
  }

  getSubscriptionStatus(params: { documentId: string }): Promise<GetSubscriptionStatusResponse> {
    return this.http
      .get<GetSubscriptionStatusResponse>(
        `/document/check-subscription-status/${params.documentId}`,
      )
      .then((res) => res.data);
  }

  sendUploadedDocumentForSigning(body: SendUploadedDocumentForSigningRequest): Promise<void> {
    return this.http.post(
      `/document/send-uploaded-document-for-signing/${this.organisationNumber}`,
      body,
    );
  }

  resendForSigning(
    params: { documentId: string; signedDocumentId: string },
    body: ResendDocumentForSigningRequest,
  ): Promise<GetDocumentSigningStatusResponse> {
    return this.http
      .post<GetDocumentSigningStatusResponse>(
        `/document/resend-for-signing/${params.documentId}/${params.signedDocumentId}`,
        body,
      )
      .then((res) => res.data);
  }

  sendForReview(params: { documentId: string; data: SendForReviewEvent }): Promise<void> {
    return this.http
      .post<void>(`/document/${params.documentId}/send-for-review`, params.data)
      .then((res) => res.data);
  }
}

export type DocumentApi = {
  getDocumentForViewing(params: {
    documentId: string;
    recipientId?: string;
    onSuccess: (data: GetDocumentForViewingResponse, state: RootStoreData) => DCEvent[];
    onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
  }): DCEvent;
};

export const documentApi: DocumentApi = {
  getDocumentForViewing: ({ documentId, recipientId, onSuccess, onFailure }) => ({
    type: "http",
    method: "get",
    url: `/document/view/${documentId}?recipientId=${recipientId}`,
    onSuccess: (res, state) => onSuccess(res.data, state),
    onFailure: (err, state) => onFailure(err, state),
  }),
};
